import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, RouterLink } from '@angular/router';

import { AdminComponentBase, PageSimpleContentComponent } from 'app/layout/content';
import { DesignerModuleName, OrderLine, OrderLineStatusFlags, OrderLineStatusValues, OrderLineUnion, SortOrder } from 'app/shared/graph';
import { FromNowPipe } from 'app/shared/utilities/from-now.pipe';

import { LineStatusComponent } from './line-status.component';
import { OrdersService } from './orders.service';

const allColumns = ['storeOrderId', 'module', 'dateCreatedUtc', 'status'];
const mobileColumns = ['storeOrderId', 'status'];
type FilterType = {
    dateOrdering: SortDirection;
    statusValues: OrderLineStatusValues[];
    startDate?: Date;
    endDate?: Date;
    module?: DesignerModuleName;
}
const saveFilterKey = 'store-orders-page-filter';

@Component({
    templateUrl: './orders-page.component.html',
    styleUrls: ['./orders-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, MatTableModule, LineStatusComponent, RouterLink, DatePipe, FromNowPipe, MatFormFieldModule, MatDatepickerModule, MatIconButton, MatIcon, FormsModule, MatSelectModule, MatPaginatorModule, MatSortModule]
})
export class OrdersPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly ordersService = inject(OrdersService);
    private readonly breakpointObserver = inject(BreakpointObserver);

    resultPerPage = 50;
    page = 0;
    hasNextPage = false;
    activeView: 'actionRequired' | 'recent' = 'recent';

    allStatusValues = Object.values(OrderLineStatusValues);
    allDesignerModules = Object.values(DesignerModuleName);
    filter: FilterType = {
        dateOrdering: 'desc',
        statusValues: []
    };
    storeId: string;

    lines: OrderLineUnion[] = [];
    displayedColumns = allColumns;

    ngOnInit(): void {
        this.activeView = this.route.snapshot.data['ordersView'];
        this.restoreFilter();
        this.registerSubscription(this.route.params.subscribe(param => {
            this.storeId = param.storeId;
            this.updateOrderList();
        }));
        this.registerSubscription(this.route.data.subscribe(param => {
            this.activeView = param['ordersView'];
            this.restoreFilter();
        }));
        this.registerSubscription(this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).subscribe((result) => {
            if (result.matches) {
                this.displayedColumns = mobileColumns;
            } else {
                this.displayedColumns = allColumns;
            }
        }));
    }

    private restoreFilter() {
        const savedFilterJson = localStorage.getItem(saveFilterKey + '-' + this.activeView);
        if (savedFilterJson) {
            const savedFilter = JSON.parse(savedFilterJson);
            if (savedFilter.startDate) {
                savedFilter.startDate = new Date(savedFilter.startDate);
            }
            if (savedFilter.endDate) {
                savedFilter.endDate = new Date(savedFilter.endDate);
            }
            this.filter = { ...this.filter, ...savedFilter as any };
            if (this.activeView == 'actionRequired') {
                this.filter.startDate = new Date(new Date().getTime() - 120 * 24 * 3600 * 1000);
            }
        }
    }

    ngOnDestroy(): void {
        this.unsubscribeSubscriptions();
    }

    clearDate() {
        this.filter.startDate = undefined;
        this.filter.endDate = undefined;
        this.updateFilter();
    }

    updateFilter() {
        this.page = 0;
        this.updateOrderList();
    }

    updateOrderList() {
        let flag: OrderLineStatusFlags | null = null;
        if (this.activeView == 'actionRequired') {
            flag = OrderLineStatusFlags.StoreActionRequired;
        }

        this.subscribeWithGenericLoadingErrorHandling(this.ordersService.getLines(
            this.storeId,
            this.filter.module,
            this.resultPerPage + 1,
            this.page * this.resultPerPage,
            this.filter.dateOrdering === 'asc' ? SortOrder.Asc : SortOrder.Desc,
            flag,
            this.filter.startDate,
            this.filter.endDate ? new Date(this.filter.endDate.getTime() + 24 * 3600 * 1000) : undefined,
            this.filter.statusValues
        ), lines => {
            this.hasNextPage = lines.length > this.resultPerPage;
            this.lines = lines.slice(0, this.resultPerPage);
        });

        this.saveFilter();
    }

    loadPage(page: number) {
        this.page = page;
        this.updateOrderList();
    }

    saveFilter() {
        localStorage.setItem(saveFilterKey + '-' + this.activeView, JSON.stringify({ ...this.filter }));
    }

    $orderLine(o: any): OrderLine {
        return o as OrderLine;
    }

    updateSort($event: Sort) {
        this.filter.dateOrdering = $event.direction;
        this.updateFilter();
    }
}
